import { saveLogin, useLoginStatus } from "@website/hooks/auth-manager";
import { addEventToDataLayerGT3 } from "@website/tracking/tracking.gtm";
import {
  DATA_LAYER_EVENTS,
  USER_TRACKING_EVENTS
} from "@website/tracking/tracking.types";
import { addEventToTracking } from "@website/tracking/user";
import {
  FORCE_LOGIN_KEY,
  validQueryStrings
} from "@website/widget/login/constants";
import { useWelcomeScreen } from "@website/widget/login/utils/useWelcomeScreen";
import { datalayerAppName } from "@website/widget/telesales/constants";
import jwtDecode from "jwt-decode";
import get from "lodash-es/get";
import omit from "lodash-es/omit";
import toString from "lodash-es/toString";
import { NextRouter, useRouter } from "next/router";
import { useCallback } from "react";

export const useLoginModal = () => {
  const queryString = "login_step";
  const router = useRouter();
  const { isWelcomeScreen } = useWelcomeScreen();
  const queryValue = router?.query?.[queryString];

  const { loginStatus } = useLoginStatus();

  const onLoginModalOpen = useCallback(() => {
    addEventToTracking(USER_TRACKING_EVENTS.LOGIN_OPENED, {
      product: "core"
    });
    router?.replace(
      {
        pathname: router?.pathname,
        query: { ...router?.query, [queryString]: "phone-number" }
      },
      undefined,
      { shallow: true, scroll: false }
    );
  }, [router]);

  const onLoginModalClose = useCallback(
    (eventTracking?: USER_TRACKING_EVENTS) => {
      document.dispatchEvent(
        new CustomEvent("onLoginModalClose", {
          detail: {}
        })
      );
      if (isWelcomeScreen && eventTracking) {
        addEventToTracking(eventTracking, { product: "core" });
        localStorage.setItem(
          "lastTimeWelcomeScreenShowed",
          new Date().toISOString()
        );
      }
      return router?.replace(
        {
          pathname: router?.pathname,
          query: omit(router?.query, [
            queryString,
            "modal",
            "login_modal",
            "login_step",
            FORCE_LOGIN_KEY
          ])
        },
        undefined,
        { shallow: true, scroll: false }
      );
    },
    [router]
  );

  const { isLoggedIn } = useLoginStatus();
  const onLoginModalEnd = useCallback(
    (accessToken?: string, refreshToken?: string) => {
      document.dispatchEvent(
        new CustomEvent("onLoginModalEnd", {
          detail: { accessToken, refreshToken }
        })
      );
      if (accessToken) {
        saveLogin({ accessToken, refreshToken });
      }
      onLoginModalClose();
    },
    [onLoginModalClose]
  );

  return {
    loginModalIsOpen: Boolean(
      typeof queryValue === "string" && validQueryStrings.has(queryValue)
    ),
    onLoginModalOpen,
    onLoginModalClose,
    onLoginModalEnd,
    loginModalProps: {
      open: Boolean(
        typeof queryValue === "string" && validQueryStrings.has(queryValue)
      ),
      onClose: router.query.modal ? undefined : onLoginModalClose,
      mobileHeight: "auto" as "full" | "auto",
      animate: "bottom" as "normal" | "bottom"
    },
    loginStatus,
    isLogin: isLoggedIn
  };
};

export const openLoginModal = (router: NextRouter) => {
  addEventToDataLayerGT3(DATA_LAYER_EVENTS.FORCE_LOGIN_CONFIRMED, {
    item: [
      {
        item_brand:
          datalayerAppName[
            process.env.NEXT_PUBLIC_APP_NAME as keyof typeof datalayerAppName
          ]
      }
    ]
  });
  return new Promise((resolve, reject) => {
    router
      .replace(
        {
          pathname: router?.pathname,
          query: {
            ...router.query,
            modal: "login",
            login_step: "phone-number"
          }
        },
        undefined,
        {
          scroll: false,
          shallow: true
        }
      )
      .then(() => {
        document.addEventListener("onLoginModalEnd", (...parameters) => {
          resolve(parameters);
          try {
            addEventToDataLayerGT3(DATA_LAYER_EVENTS.FORCE_LOGIN_SUBMITTED, {
              item: [
                {
                  phone: jwtDecode<{ phoneNumber: string }>(
                    toString(get(parameters, "detail.accessToken"))
                  ).phoneNumber,
                  item_brand:
                    datalayerAppName[
                      process.env
                        .NEXT_PUBLIC_APP_NAME as keyof typeof datalayerAppName
                    ]
                }
              ]
            });
          } catch {
            /* empty */
          }
        });
        document.addEventListener("onLoginModalClose", reject);
      });
  });
};
